import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { UserPublic } from '@csoki/shared';
import { api } from './api.js';

type AuthState = {
  user: UserPublic | null;
  loading: boolean;
  login: (email: string, pin: string) => Promise<void>;
  register: (input: {
    email: string;
    displayName: string;
    fullName: string;
    iban: string;
    phone: string;
    pin: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserPublic | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { user } = await api<{ user: UserPublic }>('GET', '/api/auth/me');
        setUser(user);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (email: string, pin: string) => {
    const { user } = await api<{ user: UserPublic }>('POST', '/api/auth/login', { email, pin });
    setUser(user);
    localStorage.setItem('csoki_last_email', user.email);
  }, []);

  const register = useCallback(
    async (input: {
      email: string;
      displayName: string;
      fullName: string;
      iban: string;
      phone: string;
      pin: string;
    }) => {
      const { user } = await api<{ user: UserPublic }>('POST', '/api/auth/register', input);
      setUser(user);
      localStorage.setItem('csoki_last_email', user.email);
    },
    [],
  );

  const logout = useCallback(async () => {
    await api('POST', '/api/auth/logout');
    setUser(null);
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, login, register, logout }}>{children}</Ctx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('AuthProvider missing');
  return ctx;
}
